import {
  intlMessages,
  unavailableItemMessages,
} from '@app/components/Messages';
import { CartDiscountBanner } from '@app/components/Messages/CartDiscountBanner';
import { MessageSeverity } from '@app/components/Messages/types';
import { messageSeverityToDesignSystemConstant } from '@app/components/Messages/utils';
import { ObjectValues } from '@app/types';
import { Type } from '@app/types/monolith/cart';
import {
  Alert,
  Box,
  CheckDiscIcon,
  constants,
  Icon,
  InfoDiscIcon,
  Stack,
  Text,
  TextLink,
} from '@redbubble/design-system';
import { useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import React from 'react';
import styles from './Messages.module.scss';

export const CART_TRAY_MSG_TYPE = {
  ...Type,
  Error: 'error',
  ItemAdded: 'item_added',
} as const;

export type CartTrayMessageType = ObjectValues<typeof CART_TRAY_MSG_TYPE>;

const IconColorBySeverity: Record<MessageSeverity, string> = {
  [MessageSeverity.Success]: 'var(--ds-color-success-600)',
  [MessageSeverity.Info]: 'var(--ds-color-information-600)',
  [MessageSeverity.Warning]: 'var(--ds-color-warning-600)',
  [MessageSeverity.Error]: 'var(--ds-color-error-600)',
};

const IconComponentByName: Record<IconName, typeof Icon> = {
  'check-disc': CheckDiscIcon,
  'info-circle': InfoDiscIcon,
} as const;

type IconName = 'check-disc' | 'info-circle';

export type CartTrayMessageData = {
  action_url?: string;
  applied?: boolean;
  discount_key?: string | null;
  discount_product_type_ids?: string[];
  product_name?: string | null;
  severity: MessageSeverity;
  text?: string;
  type: CartTrayMessageType;
  title?: string;
  priority?: Number | null;
  icon?: IconName;
};

export interface CartTrayMessageListProps {
  messages: CartTrayMessageData[];
}

type TextNodeProps = {
  text: string;
  messageType: CartTrayMessageType;
  locale: string;
  actionUrl?: string;
};

const getLocalisedHelpCenterUrl = (
  url: string,
  messageType: CartTrayMessageType,
  locale: string
) => {
  if (messageType === CART_TRAY_MSG_TYPE.NonsaleableProductRemoval) {
    return url.replace(
      '/articles/',
      `/${locale === 'en' ? 'en-us' : locale}/articles/`
    );
  }

  return url;
};

const TextNode: React.FC<TextNodeProps> = ({
  actionUrl,
  text,
  messageType,
  locale,
}) => {
  if (actionUrl) {
    return (
      <TextLink
        href={getLocalisedHelpCenterUrl(actionUrl, messageType, locale)}
      >
        {text}
      </TextLink>
    );
  }

  return <Text>{text}</Text>;
};

const IconNode = ({ icon, severity }) => {
  if (!icon || !IconComponentByName[icon]) {
    return null;
  }

  const Icon = IconComponentByName[icon];
  return <Icon size="medium" color={IconColorBySeverity[severity]} />;
};

const getMessageText = (type, text, intl): string => {
  if (type === CART_TRAY_MSG_TYPE.NonsaleableProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.nonSaleableItem);
  }
  if (type === CART_TRAY_MSG_TYPE.ExpiredProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.expiredItem);
  }
  return text;
};

const isAvailableItemDiscount = (message: CartTrayMessageData) =>
  message.type === CART_TRAY_MSG_TYPE.DiscountVolume && !message.applied;

export const CartTrayMessageList: React.FC<CartTrayMessageListProps> = ({
  messages,
}) => {
  const intl = useIntl();
  const [{ locale }] = useUserInfo<UserInfo>();

  const others = messages.filter(
    (message) => !isAvailableItemDiscount(message)
  );
  const availableItemDiscounts = messages.filter(isAvailableItemDiscount);

  return (
    <>
      {others.map(
        (
          {
            type: msgType,
            discount_key: discountKey,
            severity,
            action_url: actionUrl,
            text,
            title,
            icon,
          },
          index
        ) => {
          const alertProps: {
            intent: ObjectValues<typeof constants>;
            label: boolean;
            centered: boolean;
            title?: string;
          } = {
            intent: messageSeverityToDesignSystemConstant[severity],
            label: false,
            centered: false,
          };

          if (severity === MessageSeverity.Error) {
            if (title) alertProps.title = title;
            alertProps.label = true;
          }

          return (
            <Box
              // biome-ignore lint/suspicious/noArrayIndexKey: Message has no guaranteed unique identifier; discount_key is optional and type can duplicate
              key={`${discountKey || msgType}-${index}`}
              marginTop={index === 0 ? 'none' : 's'}
              className={styles.container}
            >
              <Alert {...alertProps}>
                <Stack spacing="xs" direction="horizontal">
                  <IconNode icon={icon} severity={severity} />
                  <TextNode
                    actionUrl={actionUrl}
                    text={getMessageText(msgType, text, intl)}
                    messageType={msgType}
                    locale={locale}
                  />
                </Stack>
              </Alert>
            </Box>
          );
        }
      )}
      {availableItemDiscounts.length > 0 && (
        <Box
          marginTop={others.length === 0 ? 'none' : 's'}
          className={styles.container}
        >
          <CartDiscountBanner
            intent={constants.INFORMATION}
            heading={intl.formatMessage(intlMessages.discountsAvailableHeading)}
            messages={availableItemDiscounts}
            getKey={(message, index) =>
              `${message.discount_key || message.type}-${index}`
            }
            getLineText={(message) =>
              getMessageText(message.type, message.text, intl)
            }
            getActionUrl={(message) =>
              message.action_url
                ? getLocalisedHelpCenterUrl(
                    message.action_url,
                    message.type,
                    locale
                  )
                : undefined
            }
          />
        </Box>
      )}
    </>
  );
};
